// RatesScreen.jsx

import React, { useState } from "react";
import { Container, Typography } from "@mui/material";

import FiltersReport from "../components/FiltersReport";
import {
    buildWeekRangePresets,
    DATATYPE_CHECKIN,
    DATATYPE_CHECKIN_NAME,
    DATATYPE_RESDATE,
    DATATYPE_RESDATE_NAME,
    DISPLAY_ELEMENT_HELPER,
    GROUP_DATE_DAY,
    GROUP_DATE_MONTH,
    GROUP_DATE_YEAR,
    GROUP_OFFERS,
    parseWeekRangePreset,
    PRESET_ANYTIME,
    process_,
} from "../helpers/filters";
import ReportBuilder, {
    GRID_HORIZONTAL,
} from "../components/shared/ReportBuilder/ReportBuilder";
import { weekDays } from "../helpers/dates";
import { TARGET_OFF } from "../helpers/targets";
import { useSelector } from "react-redux";
import { REPORT_DAY_OF_THE_WEEK_TABLE } from "../components/shared/ReportBuilder/ReportDayOfTheWeekTable";
import { REPORT_PIE_MIDWEEK_VS_WEEKEND } from "../components/shared/ReportBuilder/ReportPieMidweekVsWeekend";
import { REPORT_PIES_DISTRIBUTION_BY_RATE } from "../components/shared/ReportBuilder/ReportPiesDistributionByRate";
import { REPORT_TABLE_METRICS_SUMMARY } from "../components/shared/ReportBuilder/ReportTableMetricsSummary";
import { REPORT_WEEKDAY_RATES } from "../components/shared/ReportBuilder/ReportWeekdayRates";
import { REPORT_WEEKDAY_WEEKEND } from "../components/shared/ReportBuilder/ReportWeekdayWeekend";

const getUtcWeekdayName = (rawDate) => {
    const date = new Date(rawDate);

    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date.toLocaleDateString("en-IE", {
        weekday: "long",
        timeZone: "UTC",
    });
};

// Build the custom summary metrics for a given row set
const buildSummaryMetricsFromRows = (rows) => {
    const offers = {};
    const daysRevenue = {};
    const daysBookings = {};

    let midweekRevenue = 0;
    let weekendRevenue = 0;
    let midweekBookings = 0;
    let weekendBookings = 0;

    rows.forEach((row) => {
        const rawDate = row.selectedDate ?? row.CheckIn ?? row.ResDate;
        if (!rawDate) return;

        const date = new Date(rawDate);
        if (Number.isNaN(date.getTime())) return;

        const dayIndex = date.getUTCDay();
        const dayName = getUtcWeekdayName(rawDate);
        if (!dayName) return;

        const totalPrice = Number(row.TotalPrice ?? 0);

        if (row.Type !== "Voucher") {
            const offer = String(row.OfferTitle ?? "").trim();
            if (offer) {
                offers[offer] = (offers[offer] ?? 0) + 1;
            }
        }

        daysRevenue[dayName] = (daysRevenue[dayName] ?? 0) + totalPrice;
        daysBookings[dayName] = (daysBookings[dayName] ?? 0) + 1;

        const isWeekendDay = dayIndex === 5 || dayIndex === 6 || dayIndex === 0;

        if (isWeekendDay) {
            weekendRevenue += totalPrice;
            weekendBookings += 1;
        } else {
            midweekRevenue += totalPrice;
            midweekBookings += 1;
        }
    });

    const formatTopDayValue = (value, type = "number") => {
        if (type === "currency") {
            return new Intl.NumberFormat("en-IE", {
                style: "currency",
                currency: "EUR",
                maximumFractionDigits: 0,
            }).format(value ?? 0);
        }

        return new Intl.NumberFormat("en-IE", {
            maximumFractionDigits: 0,
        }).format(value ?? 0);
    };

    const getTopDaysWithValue = (daysMap, type = "number") => {
        const entries = Object.entries(daysMap);

        if (!entries.length) {
            return "-";
        }

        const maxValue = Math.max(...entries.map(([, value]) => value));

        const topDays = entries.filter(([, value]) => value === maxValue);

        return topDays
            .map(([day, value]) => `${day} (${formatTopDayValue(value, type)})`)
            .join(", ");
    };

    return {
        topSellingRatePlan:
            Object.entries(offers).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "-",
        topPerformingDayRevenue: getTopDaysWithValue(daysRevenue, "currency"),
        topPerformingDayBookings: getTopDaysWithValue(daysBookings, "number"),
        midweekRevenue,
        weekendRevenue,
        midweekBookings,
        weekendBookings,
    };
};

const buildOtherMetrics = (metric) => {
    return ({ js, dataBookings, hotelID, dataSimple, dataTargets }) => {
        const base = process_({
            js,
            dataBookings,
            hotelID,
            dataSimple,
            dataTargets,
        });

        const entryKeys = Object.keys(base.entries ?? {});
        if (entryKeys.length === 0) return base;

        entryKeys.forEach((entryKey) => {
            const entry = base.entries[entryKey];
            const rows = Array.isArray(entry.data) ? entry.data : [];

            // process_ sets comparisonDateGroup on comparison rows (always) but
            // never on current-period rows, so this is a reliable split regardless
            // of whether selectedDate was shifted for monthly grouping alignment.
            const currentRows = rows.filter((row) => !row.comparisonDateGroup);
            const rawComparisonRows = rows.filter(
                (row) => !!row.comparisonDateGroup,
            );

            // process_ shifts selectedDate on comparison rows so they group into
            // the matching current-period month. Restore the original pre-shift
            // date (kept in comparisonDateGroup.selectedDate) so that weekday
            // classification in buildSummaryMetricsFromRows is based on the
            // actual comparison-period date, consistent with the Total column.
            const previousRows = rawComparisonRows.map((row) => {
                const originalDate = row.comparisonDateGroup?.selectedDate;
                return originalDate && originalDate !== row.selectedDate
                    ? { ...row, selectedDate: originalDate }
                    : row;
            });

            if (
                Object.keys(entry.result ?? {}).length === 0 &&
                currentRows.length === 0
            ) {
                entry.resultOneYearBefore = {
                    ...entry.resultOneYearBefore,
                    ...buildSummaryMetricsFromRows(previousRows),
                };
                base.entries[entryKey] = entry;
                return;
            }

            const currentMetrics = buildSummaryMetricsFromRows(currentRows);
            const previousMetrics = buildSummaryMetricsFromRows(previousRows);

            entry.result = {
                ...entry.result,
                ...currentMetrics,
            };

            entry.resultOneYearBefore = {
                ...entry.resultOneYearBefore,
                ...previousMetrics,
            };

            base.entries[entryKey] = entry;
        });

        return base;
    };
};

const RatesScreen = () => {
    const { dataType } = useSelector((state) => state.globalReportFilters);
    const dateTypeDescription =
        dataType === DATATYPE_RESDATE
            ? "Booked on uses reservation date, meaning the date when the booking was made"
            : "Checked-in uses check-in date, meaning the date when the stay starts";

    const now = new Date();
    const today = new Date().toISOString().slice(0, 10);
    const from = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1),
    );
    const to = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0),
    );

    const weekOptions = buildWeekRangePresets(from, to);
    const currentWeekIndex = weekOptions.findIndex((option) => {
        const range = parseWeekRangePreset(option.value);
        if (!range) return false;

        return today >= range.fromDay && today <= range.toDay;
    });

    const weekSelected = -1;
    const targetWeekIndex =
        currentWeekIndex >= 0
            ? Math.max(
                  0,
                  Math.min(
                      weekOptions.length - 1,
                      currentWeekIndex + weekSelected,
                  ),
              )
            : 0;
    const defaultWeek = weekOptions[targetWeekIndex]?.value;

    const [metric, setMetric] = useState("Total Price");

    const REPORTS = [
        {
            type: REPORT_TABLE_METRICS_SUMMARY,
            groupBy: [],
            customProcess: buildOtherMetrics(metric),
            fieldsOrder: [
                "revenueTotal",
                "bookingsRooms",
                "roomNightsTotal",
                "topSellingRatePlan",
                "topPerformingDayRevenue",
                "topPerformingDayBookings",
                "midweekRevenue",
                "weekendRevenue",
                "midweekBookings",
                "weekendBookings",
            ],
            hideFields: Object.keys(DISPLAY_ELEMENT_HELPER).filter(
                (field) =>
                    ![
                        "revenueTotal",
                        "bookingsRooms",
                        "roomNightsTotal",
                        "ADRTotal",
                    ].includes(field),
            ),
            props: {
                hideEvents: true,
                metrics: {
                    ...DISPLAY_ELEMENT_HELPER,
                    topSellingRatePlan: {
                        short: "Top selling rate plan",
                        tooltip: false,
                        upside: false,
                    },
                    topPerformingDayRevenue: {
                        short: "Top performing day (revenue)",
                        tooltip: false,
                        upside: false,
                    },
                    topPerformingDayBookings: {
                        short: "Top performing day (bookings)",
                        tooltip: false,
                        upside: false,
                    },
                    midweekRevenue: {
                        short: "Midweek revenue",
                        // tooltip: false,
                        upside: false,
                        format: { left: "currency" },
                        headerFormat: { right: "currency", brackets: true },
                    },
                    weekendRevenue: {
                        short: "Weekend revenue",
                        // tooltip: false,
                        upside: false,
                        format: { left: "currency" },
                        headerFormat: { right: "currency", brackets: true },
                    },
                    midweekBookings: {
                        short: "Midweek bookings",
                        // tooltip: false,
                        upside: false,
                    },
                    weekendBookings: {
                        short: "Weekend bookings",
                        // tooltip: false,
                        upside: false,
                    },
                },
            },
        },
        {
            type: REPORT_DAY_OF_THE_WEEK_TABLE,
            groupBy: [
                GROUP_OFFERS,
                GROUP_DATE_DAY,
                GROUP_DATE_MONTH,
                GROUP_DATE_YEAR,
            ],
            props: {
                title: `Breakdown per KPI`,
                description: `Shows each rate plan broken down by weekday, with total revenue, bookings and room nights in separate rows. Total revenue uses Total Price, not room revenue. ${dateTypeDescription}.`,
                metric: ["Total Price", "Booking Total", "Room Nights"],
                showDate: true,
            },
        },
        {
            type: REPORT_WEEKDAY_RATES,
            title: "Rate Plan - Weekday stacked by rate",
            groupBy: [
                GROUP_OFFERS,
                GROUP_DATE_YEAR,
                GROUP_DATE_MONTH,
                GROUP_DATE_DAY,
            ],
            props: {
                metric: metric,
                description: `Shows total revenue or bookings by weekday, stacked by rate plan, so you can see which rates drive each day of the week. Revenue uses Total Price, not room revenue. ${dateTypeDescription}.`,
            },
        },
        // {
        //     type: REPORT_WEEKDAY_WEEKEND,
        //     dataType: DATATYPE_CHECKIN,
        //     secondaryDataType: DATATYPE_RESDATE,
        //     groupBy: [
        //         GROUP_OFFERS,
        //         GROUP_DATE_DAY,
        //         GROUP_DATE_MONTH,
        //         GROUP_DATE_YEAR,
        //     ],
        //     props: {
        //         metric: metric,
        //     },
        // },
        // {
        //     type: REPORT_PIES_DISTRIBUTION_BY_RATE,
        //     groupBy: [
        //         GROUP_OFFERS,
        //         GROUP_DATE_DAY,
        //         GROUP_DATE_MONTH,
        //         GROUP_DATE_YEAR,
        //     ],
        //     props: {
        //         metric: metric,
        //     },
        // },
        {
            type: GRID_HORIZONTAL,
            spacing: 2,
            md: 6,
            sm: 12,
            xs: 12,
            reports: [
                {
                    type: REPORT_PIE_MIDWEEK_VS_WEEKEND,

                    groupBy: [],
                    customProcess: buildOtherMetrics(metric),

                    props: {
                        metric: "Total Price",
                        description: `Compares total revenue generated from midweek days against weekend days. Revenue uses Total Price, not room revenue. Midweek means Monday to Thursday, and weekend means Friday to Sunday. ${dateTypeDescription}.`,
                    },
                },
                {
                    type: REPORT_PIE_MIDWEEK_VS_WEEKEND,

                    groupBy: [],
                    customProcess: buildOtherMetrics(metric),

                    props: {
                        metric: "Booking Total",
                        description: `Compares bookings generated from midweek days against weekend days. Midweek means Monday to Thursday, and weekend means Friday to Sunday. ${dateTypeDescription}.`,
                    },
                },
            ],
        },
    ];

    return (
        <>
            <Container maxWidth={false}>
                <Typography variant="h4" sx={{ mt: 2, mb: 2 }}>
                    Rates Insights
                </Typography>
                <Typography variant="subtitle1" color="initial">
                    Analyze rate performance, bookings & check-ins, and revenue
                    breakdown by weekday
                </Typography>
                <br />
                <FiltersReport
                    hideExports
                    defaultValues={{
                        dataType: DATATYPE_CHECKIN,
                        presetDate: defaultWeek,
                        presetArrivalDate: PRESET_ANYTIME,
                        presetResDate: PRESET_ANYTIME,
                        // Hide percentage only for text metrics
                        hidePercentageFor: [
                            "topSellingRatePlan",
                            "topPerformingDayRevenue",
                            "topPerformingDayBookings",
                        ],
                        filters: {
                            Target: TARGET_OFF,
                        },
                    }}
                    BookingPresetOptions={weekOptions}
                    rename={{}}
                    hide={[
                        "TIME_RANGE",
                        "FILTERS_VOUCHERS",
                        "COMPARISON",
                        "CANCELLATIONS",
                        "ARRIVAL_PERIOD",
                        "TIME_ARRIVAL_RANGE",
                        "RESDATE_PERIOD",
                        "TIME_BOOKING_RANGE",
                        "FILTERS_TYPES",
                    ]}
                    otherProps={{
                        DATATYPE_VALUES: {
                            [DATATYPE_RESDATE]: DATATYPE_RESDATE_NAME,
                            [DATATYPE_CHECKIN]: DATATYPE_CHECKIN_NAME,
                        },
                    }}
                    setProcessed={() => {}}
                    showCustomTimePeriod={false}
                />
                <ReportBuilder reports={REPORTS} />
            </Container>
        </>
    );
};

export default RatesScreen;
